import { useEffect, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { Badge } from '../../atoms/Badge/Badge';
import { Button } from '../../atoms/Button/Button';
import { Card } from '../../atoms/Card/Card';
import { Icon } from '../../atoms/Icon';
import { Inline } from '../../atoms/Inline/Inline';
import { Input } from '../../atoms/Input/Input';
import { Select } from '../../atoms/Select/Select';
import { Stack } from '../../atoms/Stack/Stack';
import { Text } from '../../atoms/Typography/Text';
import { PageHeader } from '../../templates/PageHeader/PageHeader';
import { Section } from '../../templates/Section/Section';
import { useTranslation } from '../../../i18n/useTranslation';
import { parameterLabel } from '../../measurements/measurementHistoryUtils';
import {
  confirmMeasurementExtraction,
  type MeasurementResponse,
  type PhotoExtractionResult,
} from '../../../services/measurementService';
import './MeasurementExtractionReviewForm.css';
import { Alert } from '../../atoms/Alert/Alert';
import { ApiError } from '../../../lib/apiClient';

export interface ParameterOption {
  id: string;
  name: string;
  unit: string;
}

export interface MeasurementExtractionReviewFormProps {
  aquariumId: string;
  extraction: PhotoExtractionResult;
  parameterOptions: readonly ParameterOption[];
  onConfirmed: (measurement: MeasurementResponse) => void;
  onRetakePhoto: () => void;
  onCancel: () => void;
}

type Confidence = 'high' | 'medium' | 'low' | null | undefined;

const FORM_ID = 'measurement-extraction-review-form';

function matchParameter(
  extracted: string | null,
  options: readonly ParameterOption[],
): ParameterOption | undefined {
  if (!extracted) return undefined;
  const wanted = extracted.trim().toLowerCase();
  return options.find((o) => o.id.toLowerCase() === wanted || o.name.toLowerCase() === wanted);
}

/** Field-adjacent confidence text — explicit words, never colour alone. */
function confidenceKey(confidence: Confidence, isEmpty: boolean): string {
  if (isEmpty) return 'measurement.review.confidence.needsInput';
  return confidence === 'high'
    ? 'measurement.review.confidence.high'
    : 'measurement.review.confidence.low';
}

/**
 * Review screen of the photo capture flow (epic #1438): the AI-extracted values pre-filled
 * in a fully editable form. Nothing is saved until "Confirm & save", and only the values the
 * user sees in the form are sent — never the raw extraction.
 */
export function MeasurementExtractionReviewForm({
  aquariumId,
  extraction,
  parameterOptions,
  onConfirmed,
  onRetakePhoto,
  onCancel,
}: MeasurementExtractionReviewFormProps) {
  const { t } = useTranslation();
  const matched = matchParameter(extraction.parameter, parameterOptions);
  const [parameterId, setParameterId] = useState(matched?.id ?? '');
  const [value, setValue] = useState(extraction.value !== null ? String(extraction.value) : '');
  const [unit, setUnit] = useState(extraction.unit ?? matched?.unit ?? '');
  // The reading's time is when the photo was reviewed — fixed, so a retry sends the same measurement.
  const [measuredAt] = useState(() => new Date().toISOString());
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveFailed, setSaveFailed] = useState(false);
  // The server rejected the unit (e.g. "0.01 ppb" — a ppm reading with the wrong label).
  const [unitRejected, setUnitRejected] = useState(false);
  const errorRegionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (saveFailed) errorRegionRef.current?.focus();
  }, [saveFailed]);

  const parameterMissing = parameterId === '';
  const valueMissing = value.trim() === '' || !Number.isFinite(Number(value));
  const { fieldConfidences } = extraction;

  const handleParameterChange = (id: string) => {
    setParameterId(id);
    const option = parameterOptions.find((o) => o.id === id);
    if (option && unit.trim() === '') setUnit(option.unit);
  };

  const save = async () => {
    setSubmitAttempted(true);
    if (parameterMissing || valueMissing || isSaving) return;
    const option = parameterOptions.find((o) => o.id === parameterId);
    setIsSaving(true);
    setSaveFailed(false);
    setUnitRejected(false);
    try {
      const measurement = await confirmMeasurementExtraction({
        aquariumId,
        parameterId,
        parameterName: option?.name ?? parameterId,
        value: Number(value),
        unit: unit.trim(),
        measuredAt,
        extractionId: extraction.extractionId,
        evidencePhotoKey: extraction.evidencePhotoKey,
      });
      onConfirmed(measurement);
    } catch (err) {
      if (err instanceof ApiError && err.code === 'UNIT_REJECTED') setUnitRejected(true);
      else setSaveFailed(true);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    void save();
  };

  return (
    <>
      <PageHeader
        title={t('measurement.review.title')}
        description={t('measurement.review.subtitle')}
        actions={
          saveFailed ? undefined : (
            <Button type="submit" form={FORM_ID} variant="primary" isLoading={isSaving} disabled={isSaving}>
              {t('measurement.review.confirm')}
            </Button>
          )
        }
      />
      <Section title={t('measurement.review.sectionTitle')}>
        <Card>
          <form id={FORM_ID} noValidate onSubmit={handleSubmit}>
            <Stack gap="4">
              {saveFailed && (
                <Alert
                  ref={errorRegionRef}
                  tabIndex={-1}
                  title={t('measurement.review.saveError')}
                  action={
                    <>
                      <Button type="submit" variant="primary" isLoading={isSaving} disabled={isSaving}>
                        {t('measurement.review.retrySave')}
                      </Button>
                      <Button type="button" variant="ghost" onClick={onCancel} disabled={isSaving}>
                        {t('measurement.review.cancel')}
                      </Button>
                    </>
                  }
                >
                  {t('measurement.review.saveErrorHint')}
                </Alert>
              )}

              {extraction.deviceModel ? (
                <Inline gap="2" align="center" wrap>
                  <Badge tone="info">{t('measurement.review.deviceDetectedBadge')}</Badge>
                  <Text color="muted">
                    {t('measurement.review.deviceModelValue', { model: extraction.deviceModel })}
                  </Text>
                </Inline>
              ) : (
                <Text color="muted">{t('measurement.review.deviceNotDetected')}</Text>
              )}

              {extraction.warnings.length > 0 && (
                <ul className="measurement-extraction-review__warnings">
                  {extraction.warnings.map((warning) => (
                    <li key={warning}>
                      <Text color="muted">{warning}</Text>
                    </li>
                  ))}
                </ul>
              )}

              <Select
                id="measurement-review-parameter"
                name="parameter"
                label={t('measurement.review.parameterLabel')}
                placeholder={t('measurement.review.parameterPlaceholder')}
                value={parameterId}
                onChange={(e) => handleParameterChange(e.target.value)}
                options={parameterOptions.map((o) => ({ value: o.id, label: parameterLabel(t, o.id, o.name) }))}
                required
                error={submitAttempted && parameterMissing ? t('measurement.review.needsSelection') : undefined}
                hint={
                  parameterMissing
                    ? t('measurement.review.needsSelection')
                    : t(confidenceKey(fieldConfidences.parameter, false))
                }
              />

              <Input
                id="measurement-review-value"
                name="value"
                type="number"
                inputMode="decimal"
                step="any"
                label={t('measurement.review.valueLabel')}
                value={value}
                onChange={(e) => setValue(e.target.value)}
                required
                error={submitAttempted && valueMissing ? t('measurement.review.confidence.needsInput') : undefined}
                helperText={t(confidenceKey(fieldConfidences.value, extraction.value === null))}
              />

              <Input
                id="measurement-review-unit"
                name="unit"
                label={t('measurement.review.unitLabel')}
                value={unit}
                onChange={(e) => {
                  setUnit(e.target.value);
                  setUnitRejected(false);
                }}
                error={unitRejected ? t('measurement.review.unitRejected') : undefined}
                helperText={t(confidenceKey(fieldConfidences.unit, extraction.unit === null))}
              />

              <Card variant="outlined" className="measurement-extraction-review__photo">
                <Inline gap="2" align="center">
                  <Icon name="image" ariaLabel={t('measurement.review.photoAlt')} />
                  <Text color="muted">{t('measurement.review.photoAlt')}</Text>
                </Inline>
              </Card>

              {!saveFailed && (
                <Inline gap="2" wrap>
                  <Button type="button" variant="secondary" onClick={onRetakePhoto} disabled={isSaving}>
                    {t('measurement.review.retakePhoto')}
                  </Button>
                  <Button type="button" variant="ghost" onClick={onCancel} disabled={isSaving}>
                    {t('measurement.review.cancel')}
                  </Button>
                </Inline>
              )}
            </Stack>
          </form>
        </Card>
      </Section>
    </>
  );
}
